import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { ModifierOptionRow } from "@/modules/catalog/domain/types";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { toast } from "sonner";
import { confirm } from "@/shared/lib/confirm";
import { FormRoot, FormInput, FormNumber, RHF, zodResolver } from "@/shared/forms";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";



import { useNavigate, useParams } from "@tanstack/react-router";
import {
  AlertCircleIcon,
  ArrowLeftIcon,
  ListIcon,
  PencilIcon,
  SettingsIcon,
  TrashIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { z } from "zod";
import {
  useCreateModifierOption,
  useDeleteModifierOption,
  useModifierQuery,
  useUpdateModifier,
  useUpdateModifierOption,
} from "../api/queries";
import { ModifierOptionModal } from "../ui/ModifierOptionModal";

const GeneralSchema = z
  .object({
    name: z.string().min(1, "ຕ້ອງໃສ່ຊື່ຊຸດຕົວເລືອກ"),
    minSelect: z.number().int().min(0),
    maxSelect: z.number().int().min(0),
    isRequired: z.boolean(),
  })
  .refine((d) => d.minSelect <= d.maxSelect, {
    message: "Min Select ບໍ່ສາມາດໃຫຍ່ກວ່າ Max Select",
    path: ["maxSelect"],
  });

type GeneralValues = z.infer<typeof GeneralSchema>;

function formatPriceLak(n: number): string {
  return new Intl.NumberFormat("en-US").format(n);
}

export function ModifierEditPage() {
  const { id } = useParams({ from: "/app/catalog/modifiers/$id/edit" });
  const nav = useNavigate({ from: "/app/catalog/modifiers/$id/edit" });
  const modifierId = id ?? "";

  const modifier = useModifierQuery(modifierId);
  const updateModifier = useUpdateModifier(modifierId);
  const createOption = useCreateModifierOption(modifierId);
  const [editingOptionId, setEditingOptionId] = useState("");
  const updateOption = useUpdateModifierOption(modifierId, editingOptionId);
  const deleteOption = useDeleteModifierOption(modifierId);

  const [optionModal, setOptionModal] = useState<{
    open: boolean;
    option: ModifierOptionRow | null;
  }>({ open: false, option: null });

  const generalForm = RHF.useForm<GeneralValues>({
    resolver: zodResolver(GeneralSchema),
    defaultValues: {
      name: "",
      minSelect: 0,
      maxSelect: 1,
      isRequired: false,
    },
  });

  const m = modifier.data;
  useEffect(() => {
    if (!m) return;
    generalForm.reset({
      name: m.name,
      minSelect: m.minSelect,
      maxSelect: m.maxSelect,
      isRequired: m.isRequired,
    });
  }, [
    m?.id,
    m?.name,
    m?.minSelect,
    m?.maxSelect,
    m?.isRequired,
    generalForm.reset,
  ]);

  const onSaveGeneral = generalForm.handleSubmit(async (values) => {
    try {
      await updateModifier.mutateAsync(values);
      toast.success("ບັນທຶກການປ່ຽນແປງສຳເລັດ");
    } catch (e: unknown) {
      const err = e as { message?: string };
      toast.error(err?.message ?? "ບັນທຶກລົ້ມເຫຼວ");
    }
  });

  const openAddOption = () => {
    setEditingOptionId("");
    setOptionModal({ open: true, option: null });
  };
  const openEditOption = (opt: ModifierOptionRow) => {
    setEditingOptionId(opt.id);
    setOptionModal({ open: true, option: opt });
  };

  if (modifier.isLoading || !m) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              {
                label: "ຊຸດຕົວເລືອກ",
                to: "/app/catalog/modifiers",
              },
              {
                label: "ແກ້ໄຂຊຸດຕົວເລືອກ",
              },
            ]}
          />
        </Header>
        <Main>
          <div className="text-muted-foreground">
            {modifier.isLoading ? "ກຳລັງໂຫຼດ..." : "ບໍ່ພົບຊຸດຕົວເລືອກ"}
          </div>
        </Main>
      </>
    );
  }

  const minMaxError = generalForm.formState.errors.maxSelect?.message;

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            {
              label: "ຊຸດຕົວເລືອກ",
              to: "/app/catalog/modifiers",
            },
            {
              label: "ແກ້ໄຂຊຸດຕົວເລືອກ",
            },
          ]}
        />
      </Header>
      <Main>
        {/* Toolbar */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">ແກ້ໄຂຊຸດຕົວເລືອກ</h2>
            <p className="text-muted-foreground">
              ແຜງຄວບຄຸມ / ຊຸດຕົວເລືອກ / {m.name}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => nav({ to: "/app/catalog/modifiers" })}
            >
              <ArrowLeftIcon className="size-4" />
              ກັບຄືນ
            </Button>

            <Button
              onClick={onSaveGeneral}
              isLoading={updateModifier.isPending}
            >
              ບັນທຶກການປ່ຽນແປງ
            </Button>
          </div>
        </div>

        <FormRoot<GeneralValues>
          methods={generalForm}
          onSubmit={() => {}}
          className="space-y-6"
        >
          {/* General Settings */}
          <Card className="rounded-xl border bg-card">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 font-heading text-lg">
                <SettingsIcon className="size-5 text-muted-foreground" />
                ຂໍ້ມູນທົ່ວໄປ
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <FormInput
                name="name"
                label="ຊື່ຊຸດຕົວເລືອກ"
                placeholder="ຕົວຢ່າງ: Coffee Customization"
                requiredMark
              />
              <div className="flex items-center justify-between rounded-[12px] border bg-muted/30 p-4">
                <div>
                  <p className="font-medium text-sm">ບັງຄັບເລືອກ</p>
                  <p className="text-muted-foreground text-xs">
                    ລູກຄ້າຕ້ອງເລືອກຢ່າງໜ້ອຍ 1 ຕົວເລືອກ
                  </p>
                </div>
                <RHF.Controller
                  name="isRequired"
                  control={generalForm.control}
                  render={({ field }) => (
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <FormNumber name="minSelect" label="ເລືອກຕໍ່າສຸດ" requiredMark />
                <FormNumber name="maxSelect" label="ເລືອກສູງສຸດ" requiredMark />
              </div>
              {minMaxError && (
                <div className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-destructive text-sm">
                  <AlertCircleIcon className="size-4 shrink-0" />
                  {minMaxError}
                </div>
              )}
            </CardContent>
          </Card>
        </FormRoot>

        {/* Modifier Options */}
        <Card className="mt-6 rounded-xl border bg-card">
          <CardHeader className="pb-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="flex items-center gap-2 font-heading text-lg">
                <ListIcon className="size-5 text-muted-foreground" />
                ຕົວເລືອກເພີ່ມ
              </CardTitle>
              <Button size="sm" onClick={openAddOption}>
                + ເພີ່ມຕົວເລືອກ
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ຊື່ຕົວເລືອກ</TableHead>
                  <TableHead>ລາຄາເພີ່ມ (LAK)</TableHead>
                  <TableHead>ຕົ້ນທຶນເພີ່ມ (LAK)</TableHead>
                  <TableHead className="w-24">ການດຳເນີນການ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(m.options ?? []).map((opt) => (
                  <TableRow key={opt.id}>
                    <TableCell className="font-medium">{opt.name}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      + {formatPriceLak(opt.priceDeltaLak)}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {opt.costDeltaLak != null
                        ? `+ ${formatPriceLak(opt.costDeltaLak)}`
                        : "—"}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditOption(opt)}
                        >
                          <PencilIcon className="size-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={async () => {
                            try {
                              const ok = await confirm({
                                title: "ລຶບຕົວເລືອກ",
                                description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບຕົວເລືອກນີ້?",
                                actionText: "ລຶບ",
                                ActionProps: { variant: "destructive" },
                              });
                              if (ok) {
                                await deleteOption.mutateAsync(opt.id);
                                toast.success("ລຶບຕົວເລືອກສຳເລັດ");
                              }
                            } catch {
                              toast.error("ລຶບຕົວເລືອກລົ້ມເຫຼວ");
                            }
                          }}
                          disabled={deleteOption.isPending}
                        >
                          <TrashIcon className="size-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <p className="mt-3 text-muted-foreground text-xs italic">
              ປ່ຽນລຳດັບຕົວເລືອກໄດ້ໂດຍການລາກ
            </p>
          </CardContent>
        </Card>

        {/* ใช้กับ product ไหน */}
        {m.linkedProducts && m.linkedProducts.length > 0 && (
          <Card className="mt-6 rounded-xl border bg-card">
            <CardHeader className="pb-4">
              <CardTitle className="font-heading text-lg">
                ໃຊ້ກັບສິນຄ້າໃດແດ່
              </CardTitle>
              <p className="text-muted-foreground text-sm">
                ສິນຄ້າທີ່ຜູກຊຸດຕົວເລືອກນີ້ (ອ່ານຢ່າງດຽວ)
              </p>
            </CardHeader>
            <CardContent>
              <ul className="flex flex-wrap gap-2">
                {m.linkedProducts.map((p) => (
                  <li key={p.id}>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        nav({
                          to: "/app/catalog/products/$id/edit",
                          params: { id: p.id },
                        })
                      }
                    >
                      {p.name}
                    </Button>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Bottom actions */}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t pt-6">
          <Button
            variant="outline"
            onClick={() => nav({ to: "/app/catalog/modifiers" })}
          >
            ຍົກເລີກການປ່ຽນແປງ
          </Button>
          <Button onClick={onSaveGeneral} isLoading={updateModifier.isPending}>
            ບັນທຶກຊຸດຕົວເລືອກ
          </Button>
        </div>
        <p className="mt-4 text-center text-muted-foreground text-sm">
          ຊຸດຕົວເລືອກກຳນົດກົດການປັບແຕ່ງສິນຄ້າ. ການປ່ຽນແປງຈະມີຜົນກັບທຸກສິນຄ້າທີ່ໃຊ້ຊຸດນີ້.
        </p>
      </Main>

      <ModifierOptionModal
        open={optionModal.open}
        onOpenChange={(open) => setOptionModal((prev) => ({ ...prev, open }))}
        option={optionModal.option}
        onSubmit={async (payload) => {
          if (optionModal.option) {
            await updateOption.mutateAsync(payload);
            toast.success("ແກ້ໄຂຕົວເລືອກສຳເລັດ");
          } else {
            await createOption.mutateAsync(payload);
            toast.success("ເພີ່ມຕົວເລືອກສຳເລັດ");
          }
          setOptionModal({ open: false, option: null });
          setEditingOptionId("");
        }}
        submitting={createOption.isPending || updateOption.isPending}
      />
    </>
  );
}
